// ─── Demo Skill & Tool Definitions ───────────────────────────────────────────
// All skills/tools for the three demo tabs: Form, TODO, and Flow.
// These demonstrate the power of OceanMCP's browser-side tool execution.

import {
  FUNCTION_TYPE,
  OPERATION_TYPE,
  type ExecutorFunctionDefinition,
} from "oceanmcp-shared";
import type { SkillDefinition } from "../registry/skill-registry";
import { todoStore, flowStore, formStore, tabStore, orderStore, type DemoTab } from "./demo-store";

// ─── Form Builder Skill ──────────────────────────────────────────────────────

function createFormTools(): ExecutorFunctionDefinition[] {
  return [
    {
      id: "getFormTemplates",
      name: "Get Form Templates",
      cnName: "获取表单模板",
      description:
        "Returns a list of built-in form templates (contact, survey, feedback) with their JSON Schema definitions. Use these as starting points or inspiration for creating custom forms.",
      type: FUNCTION_TYPE.EXECUTOR,
      operationType: OPERATION_TYPE.READ,
      parameters: [],
      executor: async () => {
        return {
          templates: [
            {
              name: "contact",
              title: "Contact Form",
              schema: {
                type: "object",
                required: ["name", "email", "message"],
                properties: {
                  name: { type: "string", title: "Full Name", description: "Your full name" },
                  email: { type: "string", title: "Email", format: "email", description: "your@email.com" },
                  phone: { type: "string", title: "Phone Number", description: "Optional phone number" },
                  message: { type: "string", title: "Message", format: "textarea", description: "How can we help you?" },
                },
              },
            },
            {
              name: "survey",
              title: "Customer Satisfaction Survey",
              schema: {
                type: "object",
                required: ["rating", "recommend"],
                properties: {
                  rating: {
                    type: "string",
                    title: "Overall Rating",
                    enum: ["excellent", "good", "average", "poor"],
                    enumLabels: { excellent: "Excellent", good: "Good", average: "Average", poor: "Poor" },
                  },
                  recommend: {
                    type: "string",
                    title: "Would you recommend us?",
                    enum: ["yes", "no"],
                    enumLabels: { yes: "Yes", no: "No" },
                  },
                  feedback: { type: "string", title: "Additional Feedback", format: "textarea" },
                },
              },
            },
            {
              name: "feedback",
              title: "Product Feedback",
              schema: {
                type: "object",
                required: ["product", "category"],
                properties: {
                  product: { type: "string", title: "Product Name" },
                  category: {
                    type: "string",
                    title: "Feedback Category",
                    enum: ["bug", "feature", "improvement", "other"],
                    enumLabels: { bug: "Bug Report", feature: "Feature Request", improvement: "Improvement", other: "Other" },
                  },
                  priority: {
                    type: "string",
                    title: "Priority",
                    enum: ["low", "medium", "high", "critical"],
                    enumLabels: { low: "Low", medium: "Medium", high: "High", critical: "Critical" },
                  },
                  description: { type: "string", title: "Description", format: "textarea", description: "Describe your feedback in detail" },
                },
              },
            },
          ],
        };
      },
    },
    {
      id: "previewForm",
      name: "Preview Form",
      cnName: "预览表单",
      description:
        "Takes a JSON Schema form definition and renders it in the Form Preview area on the page. " +
        "Use this to show the user what the form looks like before collecting data. " +
        "The schema should follow JSON Schema draft-7 format with type: 'object' and properties.",
      type: FUNCTION_TYPE.EXECUTOR,
      operationType: OPERATION_TYPE.WRITE,
      autoApprove: true,
      parameters: {
        type: "object",
        required: ["schema", "title"],
        properties: {
          title: {
            type: "string",
            description: "Form title displayed above the preview",
          },
          schema: {
            type: "object",
            description:
              "JSON Schema object defining the form fields. Must have type: 'object' and properties. " +
              "Supported property types: string, number, boolean. " +
              "Supported formats: textarea, email, date, time. " +
              "Use enum for select/radio fields, enumLabels for display names.",
          },
        },
        additionalProperties: false,
      },
      executor: async (args) => {
        const schema = args.schema;
        const title = typeof args.title === "string" ? args.title : "Form Preview";
        formStore.setSchema(schema, title);
        const fieldCount = schema?.properties ? Object.keys(schema.properties).length : 0;
        return {
          success: true,
          title,
          fieldCount,
          message: `Form "${title}" with ${fieldCount} fields is now displayed in the preview area.`,
        };
      },
    },
    {
      id: "collectFormData",
      name: "Collect Form Data",
      cnName: "收集表单数据",
      description:
        "Collects form data from the user using the askUser interactive form. " +
        "Pass a JSON Schema to define the form fields. The user will fill in the form and submit it. " +
        "The collected data is returned as the tool result. " +
        "IMPORTANT: You must call the askUser tool directly with the schema, not this tool. " +
        "This tool is for recording submitted form data in the preview area.",
      type: FUNCTION_TYPE.EXECUTOR,
      operationType: OPERATION_TYPE.WRITE,
      autoApprove: true,
      parameters: {
        type: "object",
        required: ["data"],
        properties: {
          data: {
            type: "object",
            description: "The submitted form data to record.",
          },
        },
        additionalProperties: false,
      },
      executor: async (args) => {
        const data = args.data ?? {};
        formStore.setSubmission(data);
        return {
          success: true,
          recordedFields: Object.keys(data).length,
          data,
          message: "Form submission recorded successfully.",
        };
      },
    },
  ];
}

export function createFormSkill(): SkillDefinition {
  return {
    name: "demo-form-builder",
    cnName: "表单构建器",
    description:
      "Build and preview dynamic forms. Create contact forms, surveys, feedback forms, or custom forms with various field types. " +
      "Use getFormTemplates to see available templates, previewForm to display a form on the page, " +
      "and askUser to collect form responses interactively.",
    instructions: `
# Form Builder Demo

You are a form builder assistant. You can create dynamic forms and display them in the preview area.

**IMPORTANT — Tab Navigation:** Before using any form tools, you MUST first call \`setCurrentTab\` with \`tab: "form"\` to switch to the Form tab so the user can see the preview. Do this as your very first step, before any other tool calls.

## Workflow

1. **Switch Tab**: Call \`setCurrentTab\` with \`tab: "form"\` to navigate to the Form tab.
2. **Get Templates**: Call \`getFormTemplates\` to see built-in templates for inspiration.
3. **Design the Form**: Based on the user's request, create a JSON Schema definition.
4. **Preview**: Call \`previewForm\` with the schema and title to show it in the preview area.
5. **Collect Data**: Use the \`askUser\` tool with the same schema to collect responses from the user interactively.
6. **Record**: After the user submits, call \`collectFormData\` to record the submission in the preview area.

## JSON Schema Format

The form schema uses JSON Schema draft-7 format:

\`\`\`json
{
  "type": "object",
  "required": ["field1"],
  "properties": {
    "field1": { "type": "string", "title": "Display Name", "description": "Placeholder text" },
    "field2": { "type": "string", "title": "Long Text", "format": "textarea" },
    "field3": { "type": "string", "title": "Select", "enum": ["a", "b"], "enumLabels": {"a": "Option A", "b": "Option B"} },
    "field4": { "type": "number", "title": "Age" },
    "field5": { "type": "boolean", "title": "Agree?", "enumLabels": {"true": "Yes", "false": "No"} },
    "field6": { "type": "string", "title": "Date", "format": "date" },
    "field7": { "type": "array", "title": "Tags", "items": { "enum": ["a","b","c"] } }
  }
}
\`\`\`

## Tips
- Always preview the form first before collecting data
- Use descriptive titles and descriptions for each field
- Mark important fields as required
- Use enumLabels for user-friendly select option display names
`,
    tools: createFormTools(),
  };
}

// ─── TODO Manager Skill ──────────────────────────────────────────────────────

function createTodoTools(): ExecutorFunctionDefinition[] {
  return [
    {
      id: "getTodoList",
      name: "Get TODO List",
      cnName: "获取待办列表",
      description:
        "Returns the current TODO list with all items, their statuses, priorities, and due dates. " +
        "Optionally filter by status or priority.",
      type: FUNCTION_TYPE.EXECUTOR,
      operationType: OPERATION_TYPE.READ,
      parameters: {
        type: "object",
        properties: {
          status: {
            type: "string",
            description: 'Filter by status: "pending", "in-progress", or "done"',
            enum: ["pending", "in-progress", "done"],
          },
          priority: {
            type: "string",
            description: 'Filter by priority: "high", "medium", or "low"',
            enum: ["high", "medium", "low"],
          },
        },
        additionalProperties: false,
      },
      executor: async (args) => {
        let items = todoStore.getAll();
        if (args.status) items = items.filter((t) => t.status === args.status);
        if (args.priority) items = items.filter((t) => t.priority === args.priority);
        return {
          total: todoStore.getAll().length,
          filtered: items.length,
          items,
        };
      },
    },
    {
      id: "addTodo",
      name: "Add TODO",
      cnName: "添加待办",
      description:
        "Adds a new TODO item to the list. The item appears immediately on the page. " +
        "Set priority to 'high', 'medium', or 'low' and optionally include a due date.",
      type: FUNCTION_TYPE.EXECUTOR,
      operationType: OPERATION_TYPE.WRITE,
      autoApprove: true,
      parameters: {
        type: "object",
        required: ["title"],
        properties: {
          title: {
            type: "string",
            description: "Task title",
          },
          description: {
            type: "string",
            description: "Optional task description with more details",
          },
          priority: {
            type: "string",
            description: "Task priority",
            enum: ["high", "medium", "low"],
            default: "medium",
          },
          dueDate: {
            type: "string",
            description: "Due date in YYYY-MM-DD format",
            format: "date",
          },
        },
        additionalProperties: false,
      },
      executor: async (args) => {
        const item = todoStore.add({
          title: args.title,
          description: args.description,
          status: "pending",
          priority: args.priority || "medium",
          dueDate: args.dueDate,
        });
        return {
          success: true,
          item,
          message: `Added "${item.title}" (${item.priority} priority) to the TODO list.`,
        };
      },
    },
    {
      id: "updateTodo",
      name: "Update TODO",
      cnName: "更新待办",
      description:
        "Updates an existing TODO item. Can change its status (pending/in-progress/done), " +
        "priority, title, description, or due date. The change reflects immediately on the page.",
      type: FUNCTION_TYPE.EXECUTOR,
      operationType: OPERATION_TYPE.WRITE,
      autoApprove: true,
      parameters: {
        type: "object",
        required: ["id"],
        properties: {
          id: {
            type: "string",
            description: "The TODO item ID (e.g., 'todo-1')",
          },
          title: { type: "string", description: "New title" },
          description: { type: "string", description: "New description" },
          status: {
            type: "string",
            description: "New status",
            enum: ["pending", "in-progress", "done"],
          },
          priority: {
            type: "string",
            description: "New priority",
            enum: ["high", "medium", "low"],
          },
          dueDate: {
            type: "string",
            description: "New due date (YYYY-MM-DD)",
            format: "date",
          },
        },
        additionalProperties: false,
      },
      executor: async (args) => {
        const { id, ...updates } = args;
        const item = todoStore.update(id, updates);
        if (!item) {
          return { success: false, error: `TODO item "${id}" not found.` };
        }
        return {
          success: true,
          item,
          message: `Updated "${item.title}" successfully.`,
        };
      },
    },
    {
      id: "deleteTodo",
      name: "Delete TODO",
      cnName: "删除待办",
      description:
        "Permanently deletes a TODO item from the list. This action cannot be undone.",
      type: FUNCTION_TYPE.EXECUTOR,
      operationType: OPERATION_TYPE.WRITE,
      parameters: {
        type: "object",
        required: ["id"],
        properties: {
          id: {
            type: "string",
            description: "The TODO item ID to delete",
          },
        },
        additionalProperties: false,
      },
      executor: async (args) => {
        const deleted = todoStore.delete(args.id);
        if (!deleted) {
          return { success: false, error: `TODO item "${args.id}" not found.` };
        }
        return {
          success: true,
          message: `Deleted TODO item "${args.id}".`,
          remaining: todoStore.getAll().length,
        };
      },
    },
    {
      id: "clearCompletedTodos",
      name: "Clear Completed TODOs",
      cnName: "清除已完成待办",
      description: "Removes all TODO items with status 'done' from the list.",
      type: FUNCTION_TYPE.EXECUTOR,
      operationType: OPERATION_TYPE.WRITE,
      parameters: [],
      executor: async () => {
        const removed = todoStore.clearCompleted();
        return {
          success: true,
          removedCount: removed,
          remaining: todoStore.getAll().length,
          message: `Cleared ${removed} completed task(s).`,
        };
      },
    },
  ];
}

export function createTodoSkill(): SkillDefinition {
  return {
    name: "demo-todo-manager",
    cnName: "待办管理器",
    description:
      "Manage a visual TODO list on the page. Add, update, complete, and delete tasks " +
      "with priorities and due dates. Changes are reflected immediately in the UI.",
    instructions: `
# TODO List Manager Demo

You are a task management assistant. You can manage a visual TODO list displayed on the page.

**IMPORTANT — Tab Navigation:** Before using any TODO tools, you MUST first call \`setCurrentTab\` with \`tab: "todo"\` to switch to the TODO List tab so the user can see the changes. Do this as your very first step, before any other tool calls.

## Available Tools

| Tool | Type | Description |
|------|------|-------------|
| getTodoList | read | Get current tasks (optionally filter by status/priority) |
| addTodo | write (auto) | Add a new task with title, priority, optional due date |
| updateTodo | write (auto) | Update task status, priority, title, etc. |
| deleteTodo | write | Permanently delete a task (requires approval) |
| clearCompletedTodos | write | Remove all completed tasks |

## Status Flow

\`pending\` → \`in-progress\` → \`done\`

## Tips

- Always check the current list with \`getTodoList\` before making bulk changes
- When the user asks to "complete" a task, update its status to "done"
- When adding multiple tasks, call \`addTodo\` for each one
- Use descriptive titles — they're displayed prominently in the UI
- Set realistic due dates when the user provides time constraints
- Priority levels: "high" (urgent), "medium" (normal), "low" (nice to have)
`,
    tools: createTodoTools(),
  };
}

// ─── React Flow Editor Skill ─────────────────────────────────────────────────

function createFlowTools(): ExecutorFunctionDefinition[] {
  return [
    {
      id: "getFlowState",
      name: "Get Flow State",
      cnName: "获取流程状态",
      description:
        "Returns the current state of the flow diagram — all nodes and edges with their properties.",
      type: FUNCTION_TYPE.EXECUTOR,
      operationType: OPERATION_TYPE.READ,
      parameters: [],
      executor: async () => {
        const state = flowStore.getState();
        return {
          nodeCount: state.nodes.length,
          edgeCount: state.edges.length,
          nodes: state.nodes,
          edges: state.edges,
        };
      },
    },
    {
      id: "addFlowNode",
      name: "Add Flow Node",
      cnName: "添加流程节点",
      description:
        "Adds a new node to the flow diagram. Specify the label, position, and node type. " +
        "The node appears immediately on the canvas. " +
        "Use type 'input' for starting nodes, 'output' for end nodes, and 'default' for intermediate steps.",
      type: FUNCTION_TYPE.EXECUTOR,
      operationType: OPERATION_TYPE.WRITE,
      autoApprove: true,
      parameters: {
        type: "object",
        required: ["label"],
        properties: {
          id: {
            type: "string",
            description: "Optional custom node ID. Auto-generated if not provided.",
          },
          label: {
            type: "string",
            description: "Text displayed inside the node",
          },
          type: {
            type: "string",
            description: "Node type: 'input' (start), 'default' (middle), 'output' (end)",
            enum: ["input", "default", "output"],
            default: "default",
          },
          x: {
            type: "number",
            description: "X position on the canvas (pixels from left). Default: auto-calculated.",
          },
          y: {
            type: "number",
            description: "Y position on the canvas (pixels from top). Default: auto-calculated.",
          },
          backgroundColor: {
            type: "string",
            description: "Optional background color (CSS color value, e.g. '#e0f2fe' or 'lightyellow')",
          },
          borderColor: {
            type: "string",
            description: "Optional border color (CSS color value)",
          },
        },
        additionalProperties: false,
      },
      executor: async (args) => {
        const nodeCount = flowStore.getState().nodes.length;
        const x = typeof args.x === "number" ? args.x : 50 + (nodeCount % 4) * 200;
        const y = typeof args.y === "number" ? args.y : 50 + Math.floor(nodeCount / 4) * 120;
        const style: Record<string, string | number> = {};
        if (args.backgroundColor) style.background = args.backgroundColor;
        if (args.borderColor) {
          style.border = `2px solid ${args.borderColor}`;
        }
        const node = flowStore.addNode({
          id: args.id,
          type: args.type || "default",
          data: { label: args.label },
          position: { x, y },
          ...(Object.keys(style).length > 0 ? { style } : {}),
        });
        return {
          success: true,
          node,
          message: `Added node "${args.label}" at position (${x}, ${y}).`,
        };
      },
    },
    {
      id: "updateFlowNode",
      name: "Update Flow Node",
      cnName: "更新流程节点",
      description:
        "Updates an existing node's label, position, type, or style. The change is reflected immediately on the canvas.",
      type: FUNCTION_TYPE.EXECUTOR,
      operationType: OPERATION_TYPE.WRITE,
      autoApprove: true,
      parameters: {
        type: "object",
        required: ["id"],
        properties: {
          id: { type: "string", description: "Node ID to update" },
          label: { type: "string", description: "New label text" },
          type: {
            type: "string",
            description: "New node type",
            enum: ["input", "default", "output"],
          },
          x: { type: "number", description: "New X position" },
          y: { type: "number", description: "New Y position" },
          backgroundColor: { type: "string", description: "New background color" },
          borderColor: { type: "string", description: "New border color" },
        },
        additionalProperties: false,
      },
      executor: async (args) => {
        const updates: any = {};
        if (args.label) updates.data = { label: args.label };
        if (args.type) updates.type = args.type;
        if (typeof args.x === "number" || typeof args.y === "number") {
          const current = flowStore.getState().nodes.find((n) => n.id === args.id);
          updates.position = {
            x: typeof args.x === "number" ? args.x : current?.position.x ?? 0,
            y: typeof args.y === "number" ? args.y : current?.position.y ?? 0,
          };
        }
        if (args.backgroundColor || args.borderColor) {
          updates.style = {};
          if (args.backgroundColor) updates.style.background = args.backgroundColor;
          if (args.borderColor) updates.style.border = `2px solid ${args.borderColor}`;
        }
        const node = flowStore.updateNode(args.id, updates);
        if (!node) {
          return { success: false, error: `Node "${args.id}" not found.` };
        }
        return { success: true, node, message: `Updated node "${args.id}".` };
      },
    },
    {
      id: "deleteFlowNode",
      name: "Delete Flow Node",
      cnName: "删除流程节点",
      description:
        "Removes a node and all its connected edges from the flow diagram.",
      type: FUNCTION_TYPE.EXECUTOR,
      operationType: OPERATION_TYPE.WRITE,
      parameters: {
        type: "object",
        required: ["id"],
        properties: {
          id: { type: "string", description: "Node ID to delete" },
        },
        additionalProperties: false,
      },
      executor: async (args) => {
        const deleted = flowStore.deleteNode(args.id);
        if (!deleted) {
          return { success: false, error: `Node "${args.id}" not found.` };
        }
        return { success: true, message: `Deleted node "${args.id}" and its connected edges.` };
      },
    },
    {
      id: "addFlowEdge",
      name: "Add Flow Edge",
      cnName: "添加流程连线",
      description:
        "Creates a connection (edge) between two nodes. Optionally add a label and animation.",
      type: FUNCTION_TYPE.EXECUTOR,
      operationType: OPERATION_TYPE.WRITE,
      autoApprove: true,
      parameters: {
        type: "object",
        required: ["source", "target"],
        properties: {
          source: { type: "string", description: "Source node ID" },
          target: { type: "string", description: "Target node ID" },
          label: { type: "string", description: "Optional label displayed on the edge" },
          animated: { type: "boolean", description: "Whether the edge is animated (dashed moving line)", default: false },
        },
        additionalProperties: false,
      },
      executor: async (args) => {
        const state = flowStore.getState();
        if (!state.nodes.find((n) => n.id === args.source)) {
          return { success: false, error: `Source node "${args.source}" not found.` };
        }
        if (!state.nodes.find((n) => n.id === args.target)) {
          return { success: false, error: `Target node "${args.target}" not found.` };
        }
        const edge = flowStore.addEdge({
          source: args.source,
          target: args.target,
          label: args.label,
          animated: args.animated ?? false,
        });
        return {
          success: true,
          edge,
          message: `Connected "${args.source}" → "${args.target}"${args.label ? ` (${args.label})` : ""}.`,
        };
      },
    },
    {
      id: "deleteFlowEdge",
      name: "Delete Flow Edge",
      cnName: "删除流程连线",
      description: "Removes an edge (connection) from the flow diagram.",
      type: FUNCTION_TYPE.EXECUTOR,
      operationType: OPERATION_TYPE.WRITE,
      parameters: {
        type: "object",
        required: ["id"],
        properties: {
          id: { type: "string", description: "Edge ID to delete" },
        },
        additionalProperties: false,
      },
      executor: async (args) => {
        const deleted = flowStore.deleteEdge(args.id);
        if (!deleted) {
          return { success: false, error: `Edge "${args.id}" not found.` };
        }
        return { success: true, message: `Deleted edge "${args.id}".` };
      },
    },
    {
      id: "layoutFlow",
      name: "Auto Layout Flow",
      cnName: "自动布局流程图",
      description:
        "Automatically arranges all nodes in a grid layout for better readability.",
      type: FUNCTION_TYPE.EXECUTOR,
      operationType: OPERATION_TYPE.WRITE,
      autoApprove: true,
      parameters: [],
      executor: async () => {
        flowStore.autoLayout();
        const state = flowStore.getState();
        return {
          success: true,
          nodeCount: state.nodes.length,
          message: `Auto-arranged ${state.nodes.length} nodes in a grid layout.`,
        };
      },
    },
    {
      id: "clearFlow",
      name: "Clear Flow",
      cnName: "清空流程图",
      description: "Removes all nodes and edges from the flow diagram. Cannot be undone.",
      type: FUNCTION_TYPE.EXECUTOR,
      operationType: OPERATION_TYPE.WRITE,
      parameters: [],
      executor: async () => {
        flowStore.clear();
        return { success: true, message: "Flow diagram cleared." };
      },
    },
  ];
}

export function createFlowSkill(): SkillDefinition {
  return {
    name: "demo-flow-editor",
    cnName: "流程图编辑器",
    description:
      "Create and edit flow diagrams on the page. Add nodes (input/default/output), " +
      "connect them with edges, and build visual workflows, pipelines, and architecture diagrams. " +
      "Changes appear instantly on the interactive canvas.",
    instructions: `
# React Flow Editor Demo

You are a flow diagram assistant. You can create and edit interactive flow diagrams displayed on the page canvas.

**IMPORTANT — Tab Navigation:** Before using any flow tools, you MUST first call \`setCurrentTab\` with \`tab: "flow"\` to switch to the React Flow tab so the user can see the canvas. Do this as your very first step, before any other tool calls.

## Available Tools

| Tool | Type | Description |
|------|------|-------------|
| getFlowState | read | Get all current nodes and edges |
| addFlowNode | write (auto) | Add a node with label, position, type, and style |
| updateFlowNode | write (auto) | Update a node's properties |
| deleteFlowNode | write | Delete a node and its edges (requires approval) |
| addFlowEdge | write (auto) | Connect two nodes with an edge |
| deleteFlowEdge | write | Delete an edge (requires approval) |
| layoutFlow | write (auto) | Auto-arrange nodes in a grid |
| clearFlow | write | Clear the entire canvas (requires approval) |

## Node Types

- **input**: Green border, used for starting/source nodes
- **default**: Standard node for intermediate steps
- **output**: Red border, used for final/sink nodes

## Positioning Strategy

The canvas is roughly 800×600 pixels. When creating a flow:

1. Start input nodes at the top (y: 50) or left (x: 50)
2. Space nodes ~200px apart horizontally, ~120px vertically
3. Keep related nodes close together
4. Use \`layoutFlow\` to auto-arrange if positioning gets messy

## Styling

- Use \`backgroundColor\` for semantic coloring (e.g., light blue for info, light yellow for warnings)
- Use \`borderColor\` to highlight important nodes
- Use \`animated: true\` on edges to show active/in-progress connections

## Best Practices

- Always check current state with \`getFlowState\` before editing existing diagrams
- Create all nodes first, then add edges to connect them
- Use meaningful labels — they're displayed inside the nodes
- Add edge labels for branching logic (e.g., "yes"/"no" on decision paths)
`,
    tools: createFlowTools(),
  };
}

// ─── Query Table Skill ───────────────────────────────────────────────────────

function createTableTools(): ExecutorFunctionDefinition[] {
  return [
    {
      id: "getFilters",
      name: "Get Table Filters",
      cnName: "获取表格筛选条件",
      description:
        "Returns the current filter state of the order query table, including all 13 filter values " +
        "and the list of valid options for each filter. Use this to understand what filters are " +
        "currently applied before modifying them.",
      type: FUNCTION_TYPE.EXECUTOR,
      operationType: OPERATION_TYPE.READ,
      parameters: [],
      executor: async () => {
        const filters = orderStore.getFilters();
        const filtered = orderStore.getFilteredOrders();
        return {
          currentFilters: filters,
          matchingOrderCount: filtered.length,
          totalOrderCount: orderStore.getAllOrders().length,
          availableOptions: {
            category: ["Electronics", "Clothing", "Home & Kitchen", "Books", "Sports", "Beauty"],
            status: ["delivered", "shipped", "processing", "cancelled", "refunded"],
            paymentMethod: ["credit_card", "paypal", "bank_transfer", "crypto"],
            shippingRegion: ["North America", "Europe", "Asia", "Oceania", "South America"],
            couponUsed: ["any", "yes", "no"],
            rating: ["", "5", "4", "3", "2", "1", "none"],
            platform: ["web", "mobile_ios", "mobile_android"],
            fulfillment: ["", "standard", "express", "same_day"],
          },
        };
      },
    },
    {
      id: "setFilters",
      name: "Set Table Filters",
      cnName: "设置表格筛选条件",
      description:
        "Sets one or more filter values on the order query table. The table updates instantly " +
        "to show only matching orders. You can set any combination of the 13 filters in a single call. " +
        "Filters not included in the call remain unchanged. " +
        "Pass an empty string or empty array to clear a specific filter.",
      type: FUNCTION_TYPE.EXECUTOR,
      operationType: OPERATION_TYPE.WRITE,
      autoApprove: true,
      parameters: {
        type: "object",
        properties: {
          search: {
            type: "string",
            description: "Free-text search across order ID, customer name, email, and product name",
          },
          category: {
            type: "array",
            description: 'Filter by product category. Array of: "Electronics", "Clothing", "Home & Kitchen", "Books", "Sports", "Beauty"',
            items: { type: "string", enum: ["Electronics", "Clothing", "Home & Kitchen", "Books", "Sports", "Beauty"] },
          },
          status: {
            type: "array",
            description: 'Filter by order status. Array of: "delivered", "shipped", "processing", "cancelled", "refunded"',
            items: { type: "string", enum: ["delivered", "shipped", "processing", "cancelled", "refunded"] },
          },
          dateFrom: {
            type: "string",
            description: "Start of date range in YYYY-MM-DD format",
          },
          dateTo: {
            type: "string",
            description: "End of date range in YYYY-MM-DD format",
          },
          amountMin: {
            type: "string",
            description: "Minimum order amount (as string, e.g. '100')",
          },
          amountMax: {
            type: "string",
            description: "Maximum order amount (as string, e.g. '500')",
          },
          paymentMethod: {
            type: "string",
            description: 'Single payment method filter: "credit_card", "paypal", "bank_transfer", "crypto", or "" for any',
            enum: ["", "credit_card", "paypal", "bank_transfer", "crypto"],
          },
          shippingRegion: {
            type: "array",
            description: 'Filter by shipping region. Array of: "North America", "Europe", "Asia", "Oceania", "South America"',
            items: { type: "string", enum: ["North America", "Europe", "Asia", "Oceania", "South America"] },
          },
          couponUsed: {
            type: "string",
            description: 'Filter by coupon usage: "any", "yes", or "no"',
            enum: ["any", "yes", "no"],
          },
          rating: {
            type: "string",
            description: 'Filter by rating: "5", "4", "3", "2", "1", "none" (not rated), or "" for any',
            enum: ["", "5", "4", "3", "2", "1", "none"],
          },
          platform: {
            type: "array",
            description: 'Filter by platform. Array of: "web", "mobile_ios", "mobile_android"',
            items: { type: "string", enum: ["web", "mobile_ios", "mobile_android"] },
          },
          fulfillment: {
            type: "string",
            description: 'Filter by fulfillment type: "standard", "express", "same_day", or "" for any',
            enum: ["", "standard", "express", "same_day"],
          },
        },
        additionalProperties: false,
      },
      executor: async (args) => {
        const updates: Record<string, any> = {};
        for (const [key, value] of Object.entries(args)) {
          if (value !== undefined) updates[key] = value;
        }
        const newFilters = orderStore.setFilters(updates);
        const filtered = orderStore.getFilteredOrders();
        return {
          success: true,
          appliedFilters: newFilters,
          matchingOrderCount: filtered.length,
          totalOrderCount: orderStore.getAllOrders().length,
          message: `Filters updated. ${filtered.length} orders match the current criteria.`,
        };
      },
    },
    {
      id: "getTableData",
      name: "Get Table Data",
      cnName: "获取表格数据",
      description:
        "Returns the currently filtered order data from the table. Use this after setting filters " +
        "to retrieve the matching orders for analysis or summarization. " +
        "Returns up to 20 rows by default; use the limit and offset parameters for pagination.",
      type: FUNCTION_TYPE.EXECUTOR,
      operationType: OPERATION_TYPE.READ,
      parameters: {
        type: "object",
        properties: {
          limit: {
            type: "number",
            description: "Maximum number of rows to return (default: 20, max: 50)",
          },
          offset: {
            type: "number",
            description: "Number of rows to skip from the start (default: 0)",
          },
        },
        additionalProperties: false,
      },
      executor: async (args) => {
        const filtered = orderStore.getFilteredOrders();
        const limit = Math.min(typeof args.limit === "number" ? args.limit : 20, 50);
        const offset = typeof args.offset === "number" ? args.offset : 0;
        const page = filtered.slice(offset, offset + limit);
        return {
          totalFiltered: filtered.length,
          totalOrders: orderStore.getAllOrders().length,
          offset,
          limit,
          returned: page.length,
          orders: page,
        };
      },
    },
  ];
}

export function createTableSkill(): SkillDefinition {
  return {
    name: "demo-query-table",
    cnName: "查询表格",
    description:
      "Query and filter an e-commerce order table with 13 filter dimensions. " +
      "Set filters by reasoning about the user's natural language query, " +
      "and retrieve the filtered results for analysis.",
    instructions: `
# Order Query Table Demo

You are a data analysis assistant. You can filter and query an e-commerce order table displayed on the page.

**IMPORTANT — Tab Navigation:** Before using any table tools, you MUST first call \`setCurrentTab\` with \`tab: "table"\` to switch to the Query Table tab so the user can see the filter changes. Do this as your very first step, before any other tool calls.

## Available Tools

| Tool | Type | Description |
|------|------|-------------|
| getFilters | read | Get current filter state and available options |
| setFilters | write (auto) | Set one or more filter values |
| getTableData | read | Retrieve filtered order rows (with pagination) |

## 13 Available Filters

| Filter | Type | Values |
|--------|------|--------|
| search | text | Free-text search across order ID, customer, email, product |
| category | multi-select | Electronics, Clothing, Home & Kitchen, Books, Sports, Beauty |
| status | multi-select | delivered, shipped, processing, cancelled, refunded |
| dateFrom | date | YYYY-MM-DD format |
| dateTo | date | YYYY-MM-DD format |
| amountMin | number string | Minimum dollar amount (e.g. "100") |
| amountMax | number string | Maximum dollar amount (e.g. "500") |
| paymentMethod | single select | credit_card, paypal, bank_transfer, crypto, or "" |
| shippingRegion | multi-select | North America, Europe, Asia, Oceania, South America |
| couponUsed | toggle | "any", "yes", or "no" |
| rating | single select | "5", "4", "3", "2", "1", "none", or "" |
| platform | multi-select | web, mobile_ios, mobile_android |
| fulfillment | single select | standard, express, same_day, or "" |

## Workflow

1. **Switch Tab**: Call \`setCurrentTab\` with \`tab: "table"\`.
2. **Understand the Query**: Parse the user's natural language request to determine which filters to set.
3. **Set Filters**: Call \`setFilters\` with the appropriate filter values. Set only what's needed — unspecified filters remain unchanged.
4. **Report Results**: After setting filters, tell the user how many orders match and summarize the key findings.
5. **Retrieve Data** (optional): Call \`getTableData\` if the user wants to see specific order details or needs data for analysis.

## Tips

- **Always reset irrelevant filters** when the user asks a new question. Pass empty strings/arrays for filters you want to clear.
- When the user says "show all", call \`setFilters\` with all filters reset to their defaults.
- Multi-select filters (category, status, shippingRegion, platform) accept arrays.
- Single-select filters (paymentMethod, rating, fulfillment) accept a single string.
- The couponUsed filter only accepts "any", "yes", or "no".
- Amount filters are strings, not numbers (e.g., "200" not 200).
- Dates must be in YYYY-MM-DD format.
`,
    tools: createTableTools(),
  };
}

// ─── Navigation Skill ────────────────────────────────────────────────────────

const VALID_TABS: DemoTab[] = ["form", "todo", "flow", "table"];

function createNavigationTools(): ExecutorFunctionDefinition[] {
  return [
    {
      id: "getCurrentTab",
      name: "Get Current Tab",
      cnName: "获取当前标签页",
      description:
        "Returns the currently active demo tab. Possible values: 'form', 'todo', 'flow', 'table'.",
      type: FUNCTION_TYPE.EXECUTOR,
      operationType: OPERATION_TYPE.READ,
      parameters: [],
      executor: async () => {
        const tab = tabStore.get();
        return {
          currentTab: tab,
          availableTabs: VALID_TABS,
          message: `The currently active tab is "${tab}".`,
        };
      },
    },
    {
      id: "setCurrentTab",
      name: "Set Current Tab",
      cnName: "切换标签页",
      description:
        "Switches the active demo tab. Use this to navigate the user to the relevant tab " +
        "before performing tab-specific operations. For example, switch to 'todo' before managing tasks, " +
        "or switch to 'flow' before creating flow diagrams.",
      type: FUNCTION_TYPE.EXECUTOR,
      operationType: OPERATION_TYPE.WRITE,
      autoApprove: true,
      parameters: {
        type: "object",
        required: ["tab"],
        properties: {
          tab: {
            type: "string",
            description: "The tab to switch to: 'todo', 'form', 'flow', or 'table'",
            enum: ["todo", "form", "flow", "table"],
          },
        },
        additionalProperties: false,
      },
      executor: async (args) => {
        const tab = args.tab as DemoTab;
        if (!VALID_TABS.includes(tab)) {
          return {
            success: false,
            error: `Invalid tab "${tab}". Valid tabs: ${VALID_TABS.join(", ")}`,
          };
        }
        const previousTab = tabStore.get();
        tabStore.set(tab);
        return {
          success: true,
          previousTab,
          currentTab: tab,
          message: previousTab === tab
            ? `Already on the "${tab}" tab.`
            : `Switched from "${previousTab}" to "${tab}" tab.`,
        };
      },
    },
  ];
}

export function createNavigationSkill(): SkillDefinition {
  return {
    name: "demo-navigation",
    cnName: "页面导航",
    description:
      "Navigate between demo tabs. Read the current tab or switch to a different one. " +
      "Use this to direct the user to the correct panel before performing domain-specific tasks.",
    instructions: `
# Tab Navigation

The demo page has four tabs, each with its own set of tools:

| Tab | Key | Description |
|-----|-----|-------------|
| TODO List | todo | Manage a visual task list with priorities and due dates |
| Form Builder | form | Build and preview dynamic forms using JSON Schema |
| React Flow | flow | Create and edit interactive flow diagrams on a canvas |
| Query Table | table | Filter and query an e-commerce order table with 13 filters |

## When to Switch Tabs

- **Always switch to the relevant tab before using its tools.** For example, if the user asks
  to "add a TODO item", call \`setCurrentTab\` with \`tab: "todo"\` first so the user can see the
  result, then use the TODO tools.
- If the user asks about forms, switch to "form" first.
- If the user asks about flow diagrams or pipelines, switch to "flow" first.
- If the user asks about orders, filtering data, or querying a table, switch to "table" first.
- Use \`getCurrentTab\` if you need to check which tab is already active.

## Tips

- Switching tabs is instant and does not lose any data — each tab's state is preserved.
- The chat is universal across all tabs, so the user can ask about any domain at any time.
- When the user's intent is ambiguous, check the current tab first — they likely want to
  interact with whichever tab they're already viewing.
`,
    tools: createNavigationTools(),
  };
}
